import { z } from "zod";
/**
 * The `askUser` tool's wire shapes (`D122`, `D123`) — the app's and the
 * `/chat/history` route's description of a question the model asked and the
 * answer that came back.
 *
 * ── WHY THIS IS A RESTATEMENT AND NOT AN IMPORT ─────────────────────────────
 * The tool itself is declared in `@repo/agents`, which sets
 * `"portableAcrossRepos": true` — `scripts/arch-check.sh` rule 12 allows that
 * module to import `@repo/kernel` and nothing else under `@repo/`, in its
 * source AND in its manifest. So it cannot import this file, and this file must
 * not import it either: `packages/shared/` imports nothing outside `shared`.
 * The same argument `ONBOARDING_ASK_STEPS` already makes for the step ids
 * [source: packages/modules/agents/src/core/trainer/onboarding-ask.ts:15-26].
 *
 * The repo's standing answer to that is restatement plus a drift guard, exactly
 * as `CHAT_FRAME` restates the SDK's frame names in `apps/mobile`. The guard
 * here is `__tests__/ask.test.ts`, and it is honest about being weaker than an
 * import: it pins the bounds and the field names as LITERALS, so a divergence
 * has to be introduced deliberately in a file that says what it is diverging
 * from. `D123` names the three restatements that must agree — the agents
 * module, this contract, and the client.
 *
 * ── WHY THE BOUNDS ARE HERE AT ALL ──────────────────────────────────────────
 * `D122`'s floor: *what the model may ask is unbounded, what it may store is
 * not*. Code refuses what the model proposes — 1–4 questions per widget, 2–8
 * options per question — and a refusal goes through the turn's existing
 * retry-with-feedback loop. This file is the copy of that floor that the app
 * and the history route parse against, so a widget that could never render
 * fails at the boundary rather than reaching a screen that has no layout for
 * it.
 *
 * ── WHAT THIS FILE DELIBERATELY DOES NOT VALIDATE ───────────────────────────
 * A step-tagged question's `step` must be in the turn's missing set, and each
 * of its option `value`s within that step's vocabulary (`D122`). Neither is
 * checkable here: both need the turn's `getOnboardingProgress` result and the
 * step vocabularies that live in `onboarding-ask.ts`. That check belongs to the
 * agent, and restating those vocabularies here would be a fourth copy nothing
 * keeps in agreement.
 */
/**
 * `D123`: one widget carries one to four questions, each with two to eight
 * options.
 *
 * Constants rather than literals in the schemas below, because the test that
 * pins them and the schema that enforces them have to be reading the same
 * number — a drift guard against a hand-copied `4` is a guard against nothing.
 */
export const ASK_MIN_QUESTIONS = 1;
export const ASK_MAX_QUESTIONS = 4;
export const ASK_MIN_OPTIONS = 2;
export const ASK_MAX_OPTIONS = 8;
/** `D191`: the chip above a question — one to three words. */
export const ASK_MAX_HEADER_LENGTH = 24;
/** `D191`: the short line under an option's label. */
export const ASK_MAX_DESCRIPTION_LENGTH = 160;
/** `D191`: what a person may type into "Outro". */
export const ASK_MAX_OTHER_LENGTH = 500;
/**
 * One option on one question.
 *
 * `label` is MODEL-AUTHORED PROSE in the conversation's locale — `D122` amends
 * `D113`'s "every label is an i18n key" for tool-originated questions only.
 * `value` is what gets written, and it stays enum-bound elsewhere: the PUT
 * schemas remain the defense, so a model that invents a label still cannot
 * write a value the server would accept.
 *
 * Both are `.min(1)`. An empty label renders as an untappable blank chip; an
 * empty value writes an empty string into an answer that reads as answered.
 */
export const askOption = z.object({
    /** `D191`: a short line under the label — what choosing it means. */
    description: z.string().min(1).max(ASK_MAX_DESCRIPTION_LENGTH).optional(),
    label: z.string().min(1),
    /** `D191`: the option the trainer recommends. Rendered first and marked. */
    recommended: z.boolean().optional(),
    value: z.string().min(1),
});
/**
 * One question in the widget.
 *
 * `step` is optional because `D122` lets the model ask what the script cannot
 * anticipate — the follow-up, the clarification. A question WITHOUT a step is a
 * conversational one and stores nothing; a question WITH one names an
 * onboarding step and is held to that step's vocabulary by the agent.
 */
export const askQuestion = z.object({
    /** `D191`: the person may type an answer none of the options says — the
     *  widget adds "Outro" with a text field, and the text comes back as the
     *  answer's `other`. Never on a step-tagged question, whose values are
     *  enum-bound (`D122`). */
    allowOther: z.boolean().optional(),
    /** `D191`: a chip of one to three words naming the question's subject. */
    header: z.string().min(1).max(ASK_MAX_HEADER_LENGTH).optional(),
    /** True when more than one option may be chosen. Absent means single-select. */
    multiSelect: z.boolean().optional(),
    options: z.array(askOption).min(ASK_MIN_OPTIONS).max(ASK_MAX_OPTIONS),
    question: z.string().min(1),
    /** An `ONBOARDING_STEPS` id when the answer is a stored step. Not an enum
     *  here on purpose — see the file header. */
    step: z.string().min(1).optional(),
});
/** The tool call's input: what the model asked. */
export const askUserInput = z.object({
    questions: z.array(askQuestion).min(ASK_MIN_QUESTIONS).max(ASK_MAX_QUESTIONS),
});
/**
 * One answered question.
 *
 * `values` and `labels` are BOTH carried, and both are needed. `values` is what
 * was written through `D114`'s direct path; `labels` is what the person
 * actually read when they tapped, in the locale the model wrote it in. Nothing
 * can recover a label from a value later — the text was model-authored, so it
 * exists nowhere else — and a restored transcript that showed raw values would
 * be showing the person something they never saw.
 */
export const askAnswer = z
    .object({
    labels: z.array(z.string()),
    /** `D191`: what the person typed into "Outro", when the question allowed
     *  it. It is the person's own text and nothing writes it anywhere — the
     *  model reads it, as it reads a message. */
    other: z.string().trim().min(1).max(ASK_MAX_OTHER_LENGTH).optional(),
    step: z.string().min(1).optional(),
    /** An answer with no values is not an answer — unless the person typed
     *  one (`other`, `D191`). A question that was skipped has no entry,
     *  rather than an empty one. */
    values: z.array(z.string()),
})
    .refine((answer) => answer.values.length > 0 || answer.other !== undefined, {
    error: "an answer needs a chosen value or typed text",
})
    /**
     * `labels` is the same length as `values`, checked rather than assumed: they
     * are two parallel arrays built by the client, and a mismatch is an
     * off-by-one that renders the wrong text beside the right value with nothing
     * failing. Refined ON the schema rather than exported as a second, stricter
     * variant — an unchecked one sitting beside it is the one somebody imports.
     */
    .refine((answer) => answer.labels.length === answer.values.length, {
    error: "labels must have one entry per value",
});
/**
 * The tool call's output: what came back.
 *
 * Bounded by the same `ASK_MAX_QUESTIONS`, because an output can never be
 * longer than the input it answers. That the two lengths are EQUAL is a
 * relation between two schemas, and this one cannot see the other — the check
 * lives where both are in hand (`askAnswersMatchQuestions`, and the agent's own
 * validation of the tool result).
 */
export const askUserOutput = z.object({
    answers: z.array(askAnswer).min(ASK_MIN_QUESTIONS).max(ASK_MAX_QUESTIONS),
});
/**
 * The one rule neither schema can carry: one answer per question, in order.
 *
 * Exported rather than inlined at each call site because there are three of
 * them — the agent validating a tool result, the history route, and the client
 * restoring a widget — and a client that pairs `questions[i]` with
 * `answers[i]` without this check renders one question's options under
 * another's prompt.
 */
export function askAnswersMatchQuestions(input, output) {
    return output.answers.length === input.questions.length;
}
//# sourceMappingURL=ask.js.map